/**
 * Slug helpers shared by the API (app ids), the portal form and the winget
 * importer. App ids are `developer-slug/app-slug` and immutable after creation.
 *
 * Dots are allowed: winget identifiers are dotted (`7zip.7zip`) and imported
 * apps keep their upstream slug as the app part of the id.
 */

/** One app id segment: lowercase, starts alphanumeric, then letters/digits/`-`/`.`. */
// '+' is legal in winget identifiers ("notepad++.notepad++") and is a path
// character, not a query separator, so it must survive validation unchanged.
const SEGMENT = '[a-z0-9][a-z0-9+-]*(?:\\.[a-z0-9][a-z0-9+-]*)*';
/** Regex source shared with zod schemas so every validator agrees. */
export const APP_ID_PATTERN_SOURCE = `^${SEGMENT}/${SEGMENT}$`;
const APP_ID_RE = new RegExp(APP_ID_PATTERN_SOURCE);

export function slugify(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

/** Slug form used for developer ids: no dots, so they stay hostname-safe. */
export function developerSlug(value: string): string {
  return slugify(value).replace(/[^a-z0-9-]/g, '');
}

export interface AppIdParts {
  developerSlug: string;
  appSlug: string;
}

export function splitAppId(appId: string): AppIdParts | null {
  if (!APP_ID_RE.test(appId)) return null;
  const index = appId.indexOf('/');
  return { developerSlug: appId.slice(0, index), appSlug: appId.slice(index + 1) };
}

export function isAppId(value: string): boolean {
  return splitAppId(value) !== null;
}

/**
 * Builds an app id from a winget PackageIdentifier (`Publisher.Package`).
 * The publisher part becomes the developer slug, the package part the app slug.
 */
export function appIdFromWinget(packageIdentifier: string): string | null {
  const [publisher, ...rest] = packageIdentifier.split('.');
  if (!publisher || rest.length === 0) return null;
  const candidate = `${slugify(publisher)}/${rest.join('.').toLowerCase()}`;
  return isAppId(candidate) ? candidate : null;
}
